import React, { useState, useEffect } from 'react';
import { Moon, Sun, Languages, Github, Mail, Heart } from 'lucide-react';
import SearchBox from './components/SearchBox';
import MovieCard from './components/MovieCard';
import { translations, detectBrowserLanguage, allLanguages, type Language } from './translations';
import { searchMoviesWithLLM, getTrendingMovies } from './api/movieApi';
import { Movie } from './types/movie';

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [language, setLanguage] = useState<Language>('zh');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Movie[]>([]);
  const [reasoning, setReasoning] = useState('');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const t = translations[language];

  // 示例搜索
  const examples = language === 'zh'
    ? ['让人心情愉悦的喜剧', '剧情反转多的悬疑片', '让人思考人生的电影']
    : ['Feel-good comedies', 'Mind-bending thrillers', 'Movies that make you think'];

  // 浏览器语言自动检测
  useEffect(() => {
    const detectedLang = detectBrowserLanguage();
    setLanguage(detectedLang);
  }, []);

  // 加载热门电影
  useEffect(() => {
    const trending = getTrendingMovies();
    setSearchResults(trending);
    setReasoning(language === 'zh' ? '热门推荐' : 'Trending Movies');
  }, [language]);

  // 切换语言
  const selectLanguage = (lang: Language) => {
    setLanguage(lang);
    setShowLanguageMenu(false);
  };

  // 搜索电影
  const handleSearch = async (query: string) => {
    setIsSearching(true);
    try {
      const response = await searchMoviesWithLLM(query);
      setSearchResults(response.movies);
      setReasoning(response.reasoning);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setReasoning(language === 'zh' ? '搜索失败，请稍后重试' : 'Search failed, please try again later');
    } finally {
      setIsSearching(false);
    }
  };

  // 点击外部关闭语言菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showLanguageMenu && !target.closest('.language-menu')) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageMenu]);

  return (
    <div className={isDarkMode ? 'dark' : ''}>
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* 顶部导航 */}
        <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <div className="text-3xl">🎬</div>
                <div>
                  <h1 className="font-bold text-xl text-gray-800 dark:text-gray-100">
                    {t.logoTitle}
                  </h1>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
                    {t.logoSubtitle}
                  </p>
                </div>
              </div>

              {/* 右侧操作 */}
              <div className="flex items-center gap-3">
                {/* 语言切换 */}
                <div className="relative language-menu">
                  <button
                    onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium text-gray-600 dark:text-gray-300"
                    aria-label="Switch Language"
                  >
                    <Languages className="w-4 h-4" />
                    <span>{t.langName}</span>
                  </button>

                  {showLanguageMenu && (
                    <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                      <div className="py-1">
                        {allLanguages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => selectLanguage(lang.code)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                              language === lang.code
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {lang.nativeName}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 主题切换 */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* 主内容区 */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero 区域 */}
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              {t.heroTitle}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              {t.heroSubtitle}
            </p>

            {/* 搜索框 */}
            <SearchBox
              onSearch={handleSearch}
              isSearching={isSearching}
              language={language}
              examples={examples}
            />
          </div>

          {/* 推荐理由 */}
          {reasoning && (
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-md">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{reasoning}</span>
              </div>
            </div>
          )}

          {/* 电影网格 */}
          {searchResults.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {searchResults.map((movie) => (
                <MovieCard
                  key={movie.id}
                  movie={movie}
                  recommendation={movie.recommendation}
                  language={language}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎭</div>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-2">
                {t.noResults}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t.tryDifferent}
              </p>
            </div>
          )}

          {/* 统计信息 */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-8 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
              <div>
                <div className="text-3xl font-bold text-orange-600">15</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.statsMovies}</div>
              </div>
              <div className="w-px h-12 bg-gray-200 dark:bg-gray-700"></div>
              <div>
                <div className="text-3xl font-bold text-green-600">100%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.statsAI}</div>
              </div>
              <div className="w-px h-12 bg-gray-200 dark:bg-gray-700"></div>
              <div>
                <div className="text-3xl font-bold text-blue-600">7x24</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.statsHours}</div>
              </div>
            </div>
          </div>
        </main>

        {/* 页脚 */}
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p className="flex items-center justify-center gap-2">
                Made with <Heart className="w-4 h-4 text-red-500" /> by {t.footerMadeWith}
              </p>
              <p>{t.footerCopyright}</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">{t.footerDisclaimer}</p>
              <div className="flex justify-center gap-6 pt-2">
                <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="hover:text-orange-600 transition-colors flex items-center gap-1">
                  <Github className="w-4 h-4" />
                  {t.footerGithub}
                </a>
                <a href="mailto:douyaworld2026@163.com" className="hover:text-orange-600 transition-colors flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {t.footerFeedback}
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
